import React, { useMemo } from 'react'
import { Row, Col, Card, Table, Progress } from 'antd'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useSelector } from 'react-redux'
import { formatCurrency } from '../../../../../utils/formatters'
import styles from './CategoryAnalysis.module.css'

const CategoryAnalysisTab = ({ dateRange }) => {
  const inventoryItems = useSelector((state) => state.inventory.items)

  const categoryData = useMemo(() => {
    const data = inventoryItems.reduce((acc, item) => {
      const category = item.category || 'Uncategorized'
      if (!acc[category]) {
        acc[category] = {
          name: category,
          itemCount: 0,
          totalValue: 0,
          averageStock: 0,
          totalStock: 0,
          items: []
        }
      }
      acc[category].itemCount += 1
      acc[category].totalValue += item.currentStock * item.unitPrice
      acc[category].totalStock += item.currentStock
      acc[category].items.push(item)
      return acc
    }, {})

    // Calculate averages and percentages
    const totalValue = Object.values(data).reduce((sum, cat) => sum + cat.totalValue, 0)
    const totalItems = inventoryItems.length

    return Object.values(data)
      .map((category) => ({
        ...category,
        averageStock: Math.round(category.totalStock / category.itemCount),
        valuePercentage: ((category.totalValue / totalValue) * 100).toFixed(1),
        itemPercentage: ((category.itemCount / totalItems) * 100).toFixed(1)
      }))
      .sort((a, b) => b.totalValue - a.totalValue)
  }, [inventoryItems])

  const columns = [
    {
      title: 'Category',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Items',
      dataIndex: 'itemCount',
      key: 'itemCount',
      sorter: (a, b) => a.itemCount - b.itemCount
    },
    {
      title: 'Total Value',
      key: 'totalValue',
      render: (_, record) => formatCurrency(record.totalValue, true),
      sorter: (a, b) => a.totalValue - b.totalValue
    },
    {
      title: 'Value Distribution',
      key: 'valueDistribution',
      render: (_, record) => (
        <Progress percent={parseFloat(record.valuePercentage)} size='small' strokeColor='#1890ff' />
      ),
      sorter: (a, b) => parseFloat(a.valuePercentage) - parseFloat(b.valuePercentage)
    },
    {
      title: 'Avg. Stock',
      dataIndex: 'averageStock',
      key: 'averageStock',
      sorter: (a, b) => a.averageStock - b.averageStock
    }
  ]

  return (
    <div className={styles.categoryAnalysisContainer}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card title='Category Value Distribution' className={styles.categoryChartCard}>
            <ResponsiveContainer width='100%' height={400}>
              <BarChart data={categoryData}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='name' />
                <YAxis />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const value = payload[0].value
                      const roundedValue = Math.round(value)

                      return (
                        <div
                          style={{
                            backgroundColor: '#fff',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                          }}
                        >
                          <p style={{ margin: 0 }}>
                            {payload[0].name}: <strong>R{roundedValue.toLocaleString('en-ZA')}</strong>
                          </p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                <Bar dataKey='totalValue' fill='#1890ff' name='Total Value' />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className={styles.categoryTableRow}>
        <Col span={24}>
          <Card title='Category Analysis' className={styles.categoryTableCard}>
            <Table dataSource={categoryData} columns={columns} rowKey='name' pagination={false} scroll={{ x: true }} />
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default CategoryAnalysisTab
